import React, { useEffect, useMemo, useState } from 'react';
import { getAccessToken } from '../utils/auth';
import { getDepartureAccuracy } from '../utils/httpApi';
import { EMPTY, fmtInt, fmtNum, fmtPercent } from '../utils/statsFormat';
import ChartCard from './charts/ChartCard';
import LineChart from './charts/LineChart';
import HeaderHint from './HeaderHint';

const RANGES = [
  { days: 7, label: '7d' },
  { days: 14, label: '14d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
  { days: 180, label: '180d' },
];

function fmtSigned(v) {
  if (v == null || !Number.isFinite(v)) return EMPTY;
  return `${v > 0 ? '+' : ''}${fmtNum(v, 1)}`;
}

/** Midnight UTC of a "YYYY-MM-DD" day, for the chart's time axis. */
function dayMs(d) {
  const t = Date.parse(`${d}T00:00:00Z`);
  return Number.isNaN(t) ? null : t;
}

/** Daily MAE series at 5 and 2 minutes out. Exported for tests. */
export function dailySeries(daily) {
  if (!Array.isArray(daily)) return [];
  const pick = (key) => daily
    .map(d => ({ ts: dayMs(d.day), v: d[key] }))
    .filter(p => p.ts != null && p.v != null);
  const t5 = pick('maeT5');
  const t2 = pick('maeT2');
  const out = [];
  if (t5.length) out.push({ name: '5 min out', slot: 1, samples: t5 });
  if (t2.length) out.push({ name: '2 min out', slot: 2, samples: t2 });
  return out;
}

/**
 * Take-off time (taxi-out) accuracy on the Accuracy page: how far the predicted take-off was from
 * the actual one, at fixed points before take-off (backend departure_accuracy, V094).
 */
export default function DepartureAccuracyPanel({ airportIcao }) {
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!airportIcao) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getDepartureAccuracy(airportIcao, days, getAccessToken());
        if (cancelled) return;
        setData(res && res.horizons ? res : null);
        setError(null);
      } catch (e) {
        if (!cancelled) setError('Could not load take-off accuracy.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [airportIcao, days]);

  const series = useMemo(() => dailySeries(data && data.daily), [data]);
  const horizons = (data && data.horizons) || [];
  const scored = data ? data.scored || 0 : 0;

  return (
    <>
      <div className="pred-accuracy-controls">
        <span className="pred-accuracy-label">Take-off time</span>
        <div className="predictions-tabs pred-accuracy-pills">
          {RANGES.map(r => (
            <button
              key={r.days}
              className={`predictions-tab${days === r.days ? ' active' : ''}`}
              onClick={() => setDays(r.days)}
            >{r.label}</button>
          ))}
        </div>
        <span className="pred-accuracy-note">
          {data && data.sessions ? `${fmtInt(scored)} of ${fmtInt(data.sessions)} departures scored` : ''}
        </span>
      </div>

      <p className="pred-accuracy-lead">
        How far our predicted take-off time was from the actual one, at fixed points before
        take-off. A departure is scored only when we know when it left: the take-off we saw
        ({data ? fmtInt(data.truthLiftoff || 0) : EMPTY}), the ground-operations record
        ({data ? fmtInt(data.truthGroundOps || 0) : EMPTY}) or the departure movement
        ({data ? fmtInt(data.truthMovement || 0) : EMPTY}).
        {data && scored > 0 && data.joinedMidTaxi != null && (
          <> {fmtPercent((100 * data.joinedMidTaxi) / scored, 0)} of them were first heard
          already taxiing, so the stand-to-taxi part is mostly unseen.</>
        )}
      </p>
      {error && <div className="pred-accuracy-hint">{error}</div>}
      <div className="pred-accuracy-table-wrap">
        <table className="predictions-table pred-accuracy-table">
          <thead>
            <tr>
              <th>When<HeaderHint text="The forecast being scored. First estimate: the session's first. Taxi start: the first made once the aircraft was moving. 10 / 5 / 2 min out: the forecast in force that long before the actual take-off. Final: the last one before take-off." /></th>
              <th className="numeric">Departures<HeaderHint text="Departures scored at this point. Fewer at 10 min out because many were first heard less than 10 minutes before they left." /></th>
              <th className="numeric">MAE<HeaderHint text="Mean absolute error in minutes between the predicted and actual take-off." /></th>
              <th className="numeric">Bias<HeaderHint text="Signed mean error: actual minus predicted. Negative = we predicted take-off too late." /></th>
              <th className="numeric">Median<HeaderHint text="Half of the departures were within this many minutes." /></th>
              <th className="numeric">p90<HeaderHint text="9 in 10 departures were within this many minutes." /></th>
              <th className="numeric">Within 2 min<HeaderHint text="Share of departures whose predicted take-off was within 2 minutes of the actual one." /></th>
            </tr>
          </thead>
          <tbody>
            {horizons.map(h => (
              <tr key={h.key} className={h.n === 0 ? 'pred-accuracy-empty-row' : ''}>
                <td>{h.label}</td>
                <td className="numeric">{h.n ? fmtInt(h.n) : EMPTY}</td>
                <td className="numeric strong">{h.maeMinutes != null ? fmtNum(h.maeMinutes, 1) : EMPTY}</td>
                <td className="numeric">{fmtSigned(h.biasMinutes)}</td>
                <td className="numeric">{h.p50Minutes != null ? fmtNum(h.p50Minutes, 1) : EMPTY}</td>
                <td className="numeric">{h.p90Minutes != null ? fmtNum(h.p90Minutes, 1) : EMPTY}</td>
                <td className="numeric">{h.within2 != null ? fmtPercent(h.within2 * 100, 0) : EMPTY}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loading && data && scored === 0 && (
        <div className="pred-accuracy-hint">
          No departures scored in this window yet. History is filled in a day at a time in the
          background, so a long window can show fewer days than it covers for a while.
        </div>
      )}

      <div className="chart-grid">
        <ChartCard
          title="Take-off accuracy per day"
          subtitle="mean absolute error at 5 and 2 minutes before take-off"
          description="One point per day. The lines should sit low and fall as the take-off gets closer; a day where the 2-minute line is no better than the 5-minute one means the forecast was not learning from the aircraft's progress. Use it to see a model change land."
          span="full"
          loading={loading}
          empty={series.length === 0}
          emptyText="Nothing scored in this period yet"
          legend={series.map(s => ({ name: s.name, slot: s.slot }))}
        >
          <LineChart series={series} unit="min" axisLabel="MAE, minutes" height={220}
                     format={(v) => fmtNum(v, 1)} />
        </ChartCard>
      </div>
    </>
  );
}
